import { NgSwitch, NgSwitchCase } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { TranslateModule } from '@ngx-translate/core';
import {
  BottomSheetState,
  GeoCoordinates,
  LocationData,
  SelectAddressData,
} from '@sc/data-access-models';
import { SandboxService } from '@sc/data-access-state-management';
import { IconComponent } from '@sc/ui-components';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import {
  ChoseNewAddressBottomSheetComponent,
  SearchLocationBottomSheetComponent,
} from '../../components';
import { getTitle } from '../../components/utils/bottom-sheet-utils';

@Component({
  selector: 'sc-select-address-bottom-sheet-container',
  styleUrls: ['./select-address-bottom-sheet.container.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    NgSwitch,
    NgSwitchCase,
    ChoseNewAddressBottomSheetComponent,
    SearchLocationBottomSheetComponent,
    TranslateModule,
  ],
  template: `
    <div class="header">
      <span class="sc-design-text-1">{{
        'SelectAddressBottomSheet.' + getTitle() | translate
      }}</span>
      <button type="button" class="close-btn" (click)="onCancel()">
        <sc-icon name="close"></sc-icon>
        <span class="sr_only">{{
          'GlobalApplicativeConstants.accessibility.closeDialog'
            | translate
              : {
                  dialogTitle:
                    'SearchLocationBottomSheet.dialogTitle' | translate
                }
        }}</span>
      </button>
    </div>

    <h1
      mat-dialog-title
      class="sr_only"
      id="select-address-bottom-sheet-title-id"
    >
      {{ 'SearchLocationBottomSheet.dialogTitle' | translate }}
    </h1>

    <div [ngSwitch]="bottomSheetState">
      <span *ngSwitchCase="'CHOSE_NEW_ADDRESS'">
        <sc-chose-new-address-bottom-sheet
          [userAddress]="data?.place?.address"
          (cancelEvent)="onCancel()"
          (pickUserPositionEvent)="onPickUserPosition($event)"
          (choseAutoCompleteAddressEvent)="onChoseAutoCompleteAddress()"
        >
        </sc-chose-new-address-bottom-sheet>
      </span>

      <span *ngSwitchCase="'GOOGLE_AUTO_COMPLETE'">
        <sc-search-location-bottom-sheet
          [locationSuggestions]="googlePlaces"
          (cancelEvent)="onCancel()"
          (valueChange)="onGoogleAutoCompleteKeyPressed($event)"
          (addressSelected)="onGoogleAutoCompleteChoseAddress($event)"
        ></sc-search-location-bottom-sheet>
      </span>
    </div>
  `,
})
export class SelectAddressBottomSheetContainer implements OnInit, OnDestroy {
  bottomSheetState: BottomSheetState = 'CHOSE_NEW_ADDRESS';

  googlePlaces: LocationData[] = [];
  private readonly addressSuggestionsSubject$ = new Subject<string>();
  private readonly subscriptions = new Subscription();

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: SelectAddressData,
    public bottomSheetRef: MatBottomSheetRef<SelectAddressBottomSheetContainer>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly sandboxService: SandboxService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.addressSuggestionsSubject$
        .pipe(
          debounceTime(500),
          filter((value) => value.length >= 3)
        )
        .subscribe((value: string) => {
          this.sandboxService.getPlacesFromAddress(value).subscribe({
            next: (places: LocationData[]) => {
              this.googlePlaces = places;
              this.changeDetectorRef.detectChanges();
            },
            error: () => {
              this.googlePlaces = [];
              this.changeDetectorRef.detectChanges();
            },
          });
        })
    );
  }

  getTitle(): string {
    return getTitle(this.bottomSheetState);
  }

  onGoogleAutoCompleteKeyPressed(autoCompleteValue: string): void {
    if (autoCompleteValue) {
      this.addressSuggestionsSubject$.next(autoCompleteValue);
    } else {
      this.googlePlaces = [];
      this.changeDetectorRef.detectChanges();
    }
  }

  onGoogleAutoCompleteChoseAddress(place: LocationData): void {
    this.bottomSheetRef.dismiss({ place });
  }

  onPickUserPosition(geoCodeData: GeoCoordinates): void {
    this.sandboxService
      .getPlaceDataFromGeoCode(geoCodeData.lat, geoCodeData.lon)
      .pipe(take(1))
      .subscribe((placeData: LocationData) => {
        this.data.place = placeData;
        this.changeDetectorRef.detectChanges();
        this.bottomSheetRef.dismiss({ place: this.data.place });
      });
  }

  onChoseAutoCompleteAddress(): void {
    this.bottomSheetState = 'GOOGLE_AUTO_COMPLETE';
  }

  onChoseAddressManually() {}

  onCancel(): void {
    this.bottomSheetRef.dismiss();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.addressSuggestionsSubject$.complete();
  }
}
